import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Footer from "../../layout/Footer/Footer";

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const resetToken = searchParams.get("token"); // รับ reset token จาก URL
    const [formData, setFormData] = useState({
        newPassword: "",
        confirmPassword: "",
    });
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState({
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false
    });

    const validatePassword = (password) => {
        const validations = {
            minLength: password.length >= 8,
            hasUpperCase: /[A-Z]/.test(password),
            hasLowerCase: /[a-z]/.test(password),
            hasNumber: /[0-9]/.test(password)
        };

        return {
            isValid: Object.values(validations).every(Boolean),
            validations
        };
    };

    useEffect(() => {
        if (!resetToken) {
            setError("Invalid reset token");
            setIsDisabled(true);
        }
    }, [resetToken]);

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'newPassword') {
            const { validations } = validatePassword(value);
            setPasswordStrength(validations);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!resetToken) {
            setError("Invalid reset token");
            return;
        }

        const { isValid, validations } = validatePassword(formData.newPassword);
        if (!isValid) {
            let errorMessage = "Password must:";
            if (!validations.minLength) errorMessage += "\n- Be at least 8 characters long";
            if (!validations.hasUpperCase) errorMessage += "\n- Contain at least one uppercase letter";
            if (!validations.hasLowerCase) errorMessage += "\n- Contain at least one lowercase letter";
            if (!validations.hasNumber) errorMessage += "\n- Contain at least one number";
            setError(errorMessage);
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/auth/reset-password`,
                {
                    token: resetToken,
                    newPassword: formData.newPassword,
                }
            );
            console.log('reset password response:', response.data);

            setSuccess(response.data.message);
            setFormData({
                newPassword: "",
                confirmPassword: "",
            });

            setIsDisabled(true);

            // Redirect to login after success
            setTimeout(() => {
                navigate("/login");
            }, 3000);
        } catch (err) {
            if (err.response?.data?.errors) {
                // แสดง error messages ทั้งหมดที่ได้จาก validation
                setError(err.response.data.errors.join('\n'));
            } else if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError("Failed to reset password. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };
    if (!resetToken) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="alert alert-error">
                    <span>Invalid reset link. Please request a new password reset.</span>
                </div>
            </div>
        );
    }
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
            <form
                onSubmit={handleSubmit}
                className="card bg-base-100 w-full max-w-sm shadow-xl p-6"
            >
                <h2 className="text-xl font-bold mb-4">Reset Password</h2>
                {success && <p className="text-green-500 text-sm mb-4">{success}</p>}
                {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
                <div className="form-control">
                    <label
                        htmlFor="newPassword"
                        className="block text-sm font-medium text-gray-900 mb-2"
                    >
                        New Password
                    </label>
                    <input
                        id="newPassword"
                        name="newPassword"
                        type="password"
                        placeholder="Enter new password"
                        className="input input-bordered w-full mb-4"
                        value={formData.newPassword}
                        onChange={handlePasswordChange}
                        disabled={isLoading || isDisabled}
                        required
                        minLength={8}
                    />
                </div>
                <div className="form-control">
                    <label
                        htmlFor="confirmPassword"
                        className="block text-sm font-medium text-gray-900 mb-2"
                    >
                        Confirm Password
                    </label>
                    <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        placeholder="Confirm new password"
                        className="input input-bordered w-full mb-4"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        disabled={isLoading || isDisabled}
                        required
                        minLength={8}
                    />
                </div>
                <button
                    type="submit"
                    className={`btn btn-neutral bg-black hover:bg-gray-800 w-full ${isLoading || isDisabled ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                    disabled={isLoading || isDisabled || !resetToken}
                >
                    {isLoading ? (
                        <span className="loading loading-dots loading-lg"></span>
                    ) : (
                        "Reset Password"
                    )}
                </button>
                <div className="bg-gray-50 p-4 rounded-lg mb-4 text-sm mt-2">
                    <p className="font-medium mb-2">Password Requirements:</p>
                    <ul className="space-y-1">
                        <li className={`flex items-center ${passwordStrength.minLength ? 'text-green-600' : 'text-gray-600'}`}>
                            <span className={`inline-block w-4 h-4 mr-2 rounded-full ${passwordStrength.minLength ? 'bg-green-600' : 'bg-gray-300'
                                }`}></span>
                            At least 8 characters long
                        </li>
                        <li className={`flex items-center ${passwordStrength.hasUpperCase ? 'text-green-600' : 'text-gray-600'}`}>
                            <span className={`inline-block w-4 h-4 mr-2 rounded-full ${passwordStrength.hasUpperCase ? 'bg-green-600' : 'bg-gray-300'
                                }`}></span>
                            Contains uppercase letter (A-Z)
                        </li>
                        <li className={`flex items-center ${passwordStrength.hasLowerCase ? 'text-green-600' : 'text-gray-600'}`}>
                            <span className={`inline-block w-4 h-4 mr-2 rounded-full ${passwordStrength.hasLowerCase ? 'bg-green-600' : 'bg-gray-300'
                                }`}></span>
                            Contains lowercase letter (a-z)
                        </li>
                        <li className={`flex items-center ${passwordStrength.hasNumber ? 'text-green-600' : 'text-gray-600'}`}>
                            <span className={`inline-block w-4 h-4 mr-2 rounded-full ${passwordStrength.hasNumber ? 'bg-green-600' : 'bg-gray-300'
                                }`}></span>
                            Contains number (0-9)
                        </li>
                    </ul>
                </div>
            </form>
            <Footer />
        </div>
    );
}
